// ─────────────────────────────────────────────
//  ProfileSettings.jsx
//  Connected to: ProfileLogic.js, Profile.css
//  Used by: Profile.jsx
// ─────────────────────────────────────────────

import React, { useState, useEffect, useRef } from "react";
import { Icons } from "./Profile";
import UserService from "../../../services/userService";
import { COUNTRY_CODES } from "./ProfileLogic";
import { useOtpVerification } from "./Auth";

function EyeIcon({ open }) {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// ────────────────────────────────────────────
//  OTP BOXES
// ────────────────────────────────────────────
function OtpBoxes({ value = '', onChange, length = 6 }) {
  const refs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  function handleChange(e, idx) {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) return;
    const ch = raw[raw.length - 1];
    const next = digits.map((d, i) => (i === idx ? ch : d)).join('');
    onChange(next);
    if (idx < length - 1) refs.current[idx + 1]?.focus();
  }

  function handleKey(e, idx) {
    if (e.key === 'Backspace') {
      if (digits[idx] !== '') {
        onChange(digits.map((d, i) => (i === idx ? '' : d)).join(''));
      } else if (idx > 0) {
        refs.current[idx - 1]?.focus();
      }
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, length - 1);
    refs.current[focusIdx]?.focus();
  }

  return (
    <div className="ProfileOtpBoxes">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className="ProfileOtpBox"
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKey(e, i)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}

// ────────────────────────────────────────────
//  SETTINGS TAB — default export
// ────────────────────────────────────────────
function splitName(full) {
  const parts = (full || '').trim().split(/\s+/);
  const first = parts[0] || '';
  const last  = parts.slice(1).join(' ');
  return { first, last };
}

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

export default function SettingsTab({ user, onSave, onLogout }) {
  const originalEmail = useRef(user?.email || '');
  const originalPhone = useRef(user?.phone || '');
  const [name, setName]           = useState(user?.name || '');
  const [firstName, setFirstName] = useState(() => splitName(user?.name).first);
  const [lastName, setLastName]   = useState(() => splitName(user?.name).last);
  const [email, setEmail]         = useState(user?.email || '');
  const [phone, setPhone]         = useState(user?.phone || '');
  const [phoneCode, setPhoneCode] = useState(user?.phoneCode || '+91');
  const [address, setAddress]     = useState(user?.address || '');

  const [pinCode, setPinCode]     = useState(user?.pinCode || '');

  const [editMode, setEditMode]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState('');

  const [pwdToast, setPwdToast]         = useState(false);
  const [pwdOpen, setPwdOpen]           = useState(false);
  const [oldPassword, setOldPassword]   = useState('');
  const [newPassword, setNewPassword]   = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld]           = useState(false);
  const [showNew, setShowNew]           = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [pwdSaving, setPwdSaving]       = useState(false);
  const [pwdMsg, setPwdMsg]             = useState({ text: '', error: false });
  const [currentPwdError, setCurrentPwdError]   = useState('');
  const [currentPwdChecking, setCurrentPwdChecking] = useState(false);

  const [showOtpSection, setShowOtpSection]   = useState(false);
  const [pwdOtpVisible, setPwdOtpVisible]     = useState(false);

  const otp = useOtpVerification();
  const pwdOtp = useOtpVerification();

  useEffect(() => {
    UserService.getProfile().then((p) => {
      if (!p) return;
      if (p.name)                        setName(p.name);
      if (p.email)                       { setEmail(p.email); originalEmail.current = p.email; }
      if (p.phone)                       { setPhone(p.phone); originalPhone.current = p.phone; }
      if (p.phone_code || p.phoneCode)   setPhoneCode(p.phone_code || p.phoneCode);
      if (p.address)                     setAddress(p.address);

      if (p.pin_code || p.pinCode)       setPinCode(p.pin_code || p.pinCode);
    }).catch(() => {});
  }, []);

  const otpTargetEmail = originalEmail.current || email.trim();
  const otpTargetPhone = originalPhone.current || phone.trim();

  async function handleSendOtp(method) {
    if (method === 'email') {
      if (!otpTargetEmail) { alert("No existing email to verify."); return; }
    } else {
      if (!otpTargetPhone) { alert("No existing phone number to verify."); return; }
    }
    const target = method === 'email' ? undefined : { phone: otpTargetPhone, phoneCode };
    const res = await otp.sendOtp(method, target);
    if (!res.success) alert(res.error);
  }

  async function handleVerifyOtp() {
    if (!otp.otp || otp.otp.length < 6) { alert("Please enter the 6-digit OTP."); return; }
    const target = otp.method === 'email' ? otpTargetEmail : { phone: otpTargetPhone, phoneCode };
    const res = await otp.verifyOtp(target);
    if (!res.success) alert(res.error);
  }

  async function handlePwdSendOtp(method) {
    if (method === 'email') {
      if (!otpTargetEmail) { alert("No existing email to verify."); return; }
    } else {
      if (!otpTargetPhone) { alert("No existing phone number to verify."); return; }
    }
    const target = method === 'email' ? undefined : { phone: otpTargetPhone, phoneCode };
    const res = await pwdOtp.sendOtp(method, target);
    if (!res.success) alert(res.error);
  }

  async function handlePwdVerifyOtp() {
    if (!pwdOtp.otp || pwdOtp.otp.length < 6) { alert('Please enter the 6-digit OTP.'); return; }
    const target = pwdOtp.method === 'email' ? otpTargetEmail : { phone: otpTargetPhone, phoneCode };
    const res = await pwdOtp.verifyOtp(target);
    if (!res.success) alert(res.error);
  }

  function handleEdit() {
    const { first, last } = splitName(name);
    setFirstName(first);
    setLastName(last);
    setEditMode(true);
    setShowOtpSection(false);
    otp.reset();
  }

  function handleCancel() {
    const n = user?.name || '';
    setName(n);
    const { first, last } = splitName(n);
    setFirstName(first); setLastName(last);
    setEmail(user?.email || '');
    setPhone(user?.phone || '');
    originalEmail.current = user?.email || '';
    originalPhone.current = user?.phone || '';
    setPhoneCode(user?.phoneCode || '+91');
    setAddress(user?.address || '');
    setPinCode(user?.pinCode || '');
    setEditMode(false);
    setShowOtpSection(false);
    otp.reset();
  }

  async function checkCurrentPassword() {
    if (!oldPassword) { setCurrentPwdError(''); return; }
    setCurrentPwdChecking(true);
    try {
      const result = await UserService.verifyPassword(oldPassword);
      setCurrentPwdError(result.match ? '' : 'Incorrect password. Please try again.');
    } catch {
      setCurrentPwdError('');
    } finally {
      setCurrentPwdChecking(false);
    }
  }

  async function handleChangePassword() {
    setPwdSaving(true);
    setPwdMsg({ text: '', error: false });
    try {
      await UserService.changePassword(oldPassword, newPassword);
      setPwdMsg({ text: '', error: false });
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
      pwdOtp.reset();
      setPwdOpen(false);
      setPwdToast(true);
      setTimeout(() => setPwdToast(false), 4000);
    } catch (err) {
      setPwdMsg({ text: err.response?.data?.message || 'Failed to change password.', error: true });
    } finally {
      setPwdSaving(false);
    }
  }

  async function handleSaveChanges() {
    setSaving(true);
    try {
      const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
      setName(fullName);
      await onSave({ name: fullName, email: email.trim(), phone: phone.trim(), phoneCode, address: address.trim(), pinCode: pinCode.trim() });
      setSaveMsg("Details updated successfully!");
      setEditMode(false);
      setShowOtpSection(false);
      otp.reset();
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ProfileInfoCard">
      {pwdToast && (
        <div className="ProfilePwdToast">
          <span className="ProfilePwdToastIcon">✓</span>
          Password updated successfully!
        </div>
      )}
      <div className="ProfileSettingsUserIdHero">
        <span className="ProfileSettingsUserIdLabel">User ID</span>
        <span className="ProfileSettingsUserIdValue">{user?.human_id || user?.userId || "—"}</span>
      </div>
      <div className="ProfileInfoCardHeader">
        <h3>Account Settings</h3>
        {!editMode && (
          <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm" onClick={handleEdit}>
            <Icons.Edit /> Edit
          </button>
        )}
      </div>

      {saveMsg && <div className="ProfileSettingsSaveMsg">{saveMsg}</div>}

      <div className="ProfileSettingsForm">

        {/* ── Editable fields ── */}
        {editMode ? (
          <>
            <div className="ProfileInfoField">
              <label>First Name</label>
              <input autoComplete="off" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
            </div>
            <div className="ProfileInfoField">
              <label>Last Name</label>
              <input autoComplete="off" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
            </div>
          </>
        ) : (
          <div className="ProfileInfoField ProfileSettingsSpan">
            <label>Full Name</label>
            <div className="ProfileFieldValue">{name || "—"}</div>
          </div>
        )}

        <div className="ProfileInfoField">
          <label>Email Address</label>
          {editMode
            ? <input type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
            : <div className="ProfileFieldValue">{email || "—"}</div>}
        </div>

        <div className="ProfileInfoField">
          <label>Phone Number</label>
          {editMode
            ? (
              <div className="ProfileSettingsPhoneRow">
                <div className="ProfileSettingsCodeWrap">
                  <select className="ProfileSettingsCodeSelect" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)}>
                    {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                  </select>
                  <span className="ProfileSettingsCodeChevron">▾</span>
                </div>
                <input type="tel" autoComplete="off" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="Phone number" />
              </div>
            )
            : <div className="ProfileFieldValue">{phone ? `${phoneCode} ${phone}` : "—"}</div>}
        </div>

        <div className="ProfileInfoField">
          <label>Address</label>
          {editMode
            ? <input autoComplete="off" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street address" />
            : <div className="ProfileFieldValue">{address || "—"}</div>}
        </div>

        <div className="ProfileInfoField">
          <label>Pin Code</label>
          {editMode
            ? <input autoComplete="off" value={pinCode} onChange={(e) => setPinCode(e.target.value.replace(/\D/g, ''))} placeholder="Pin code" maxLength={10} />
            : <div className="ProfileFieldValue">{pinCode || "—"}</div>}
        </div>

        {/* ── OTP Section ── */}
        {editMode && showOtpSection && !otp.verified && (
          <div className="ProfileSettingsOtpSection ProfileSettingsSpan">
            <p className="ProfileSettingsOtpLabel">Verify your identity to save changes</p>
            {!otp.sent && (
              <div className="ProfileSettingsOtpBtnRow">
                <button type="button" className="ProfileBtn ProfileBtnOutline ProfileBtnSm" onClick={() => handleSendOtp('email')} disabled={otp.sending || !otpTargetEmail}>
                  {otp.sending && otp.method === 'email' ? '...' : 'Verify via Email'}
                </button>
                <button type="button" className="ProfileBtn ProfileBtnOutline ProfileBtnSm" onClick={() => handleSendOtp('phone')} disabled={otp.sending || !otpTargetPhone}>
                  {otp.sending && otp.method === 'phone' ? '...' : 'Verify via Phone'}
                </button>
              </div>
            )}
            {otp.sent && (
              <div className="ProfileSettingsOtpCenter">
                <p className="ProfileSettingsOtpHint">OTP sent to {otp.method === 'email' ? otpTargetEmail : `${phoneCode} ${otpTargetPhone}`}</p>
                <OtpBoxes value={otp.otp} onChange={otp.setOtp} />
                <button type="button" className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={handleVerifyOtp} disabled={otp.verifying || otp.otp.length < 6}>
                  {otp.verifying ? 'Verifying...' : 'Verify OTP'}
                </button>
                <div className="ProfileSettingsOtpTimer">
                  {otp.timerActive
                    ? <span>Resend in {formatTime(otp.timer)}</span>
                    : <button type="button" className="ProfileSettingsResendBtn" onClick={() => otp.reset()}>Try again</button>}
                </div>
              </div>
            )}
          </div>
        )}

        {editMode && otp.verified && (
          <div className="ProfileSettingsVerifiedBadge ProfileSettingsSpan">
            {otp.method === 'email' ? 'Email Verified' : 'Phone Verified'} ✓
          </div>
        )}

        {/* ── Action buttons ── */}
        {editMode && (
          <div className="ProfileSettingsActions ProfileSettingsSpan">
            {!otp.verified && (
              <button
                className="ProfileBtn ProfileBtnPrimary"
                type="button"
                onClick={() => setShowOtpSection(true)}
                disabled={saving}
              >
                Verify &amp; Save
              </button>
            )}
            {otp.verified && (
              <button
                className="ProfileBtn ProfileBtnPrimary"
                type="button"
                onClick={handleSaveChanges}
                disabled={saving}
              >
                {saving ? <span className="ProfileSpinner" /> : 'Save Changes'}
              </button>
            )}
            <button className="ProfileBtn ProfileBtnOutline" type="button" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        )}

        <div className="ProfileSettingsDivider ProfileSettingsSpan" />

        {/* ── Read-only identity fields ── */}
        <div className="ProfileInfoField">
          <label>Account Type</label>
          <div className="ProfileFieldValue">
            {user?.role === "individual" ? "Individual" : user?.role === "member" ? "Member" : user?.role === "agent" ? "Agent" : "—"}
          </div>
        </div>
        <div className="ProfileInfoField">
          <label>Account Status</label>
          <div className="ProfileFieldValue ProfileFieldValue--active">Active &amp; Verified</div>
        </div>

        {/* ── Change Password ── */}
        {user?.authProvider !== 'google' && (
        <div className="ProfileSettingsPwdSection ProfileSettingsSpan">
          <button
            type="button"
            className="ProfileSettingsPwdToggle"
            onClick={() => { setPwdOpen((v) => !v); setPwdMsg({ text: '', error: false }); pwdOtp.reset(); }}
          >
            <Icons.Lock /> Change Password
            <span className="ProfileSettingsPwdChevron">{pwdOpen ? '▲' : '▼'}</span>
          </button>

          {pwdOpen && (() => {
            const reqs = [
              { label: 'At least 8 characters',       met: newPassword.length >= 8 },
              { label: 'One uppercase letter (A–Z)',   met: /[A-Z]/.test(newPassword) },
              { label: 'One lowercase letter (a–z)',   met: /[a-z]/.test(newPassword) },
              { label: 'One number (0–9)',             met: /[0-9]/.test(newPassword) },
              { label: 'One special character (!@#…)', met: /[^A-Za-z0-9]/.test(newPassword) },
            ];
            const score = reqs.filter((r) => r.met).length;
            const strengthLabel = score <= 1 ? 'Weak' : score === 2 ? 'Fair' : score === 3 ? 'Moderate' : score === 4 ? 'Good' : 'Strong';
            const strengthColor = score <= 1 ? 'var(--Profile-red)' : score === 2 ? '#f97316' : score === 3 ? '#eab308' : score === 4 ? '#84cc16' : 'var(--Profile-green)';
            const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
            const passwordsMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
            const sameAsCurrent = oldPassword.length > 0 && newPassword.length > 0 && newPassword === oldPassword;
            const fieldsReady = oldPassword.length > 0 && !currentPwdError && !currentPwdChecking && score >= 3 && passwordsMatch && !sameAsCurrent;
            return (
              <div className="ProfileSettingsPwdForm">
                <div className="ProfilePwdGrid">

                  {/* ── Left column ── */}
                  <div className="ProfilePwdCol">
                    <div className="ProfileSettingsPwdField">
                      <label>Current Password</label>
                      <div className="ProfileSettingsPwdInputWrap">
                        <input
                          type={showOld ? 'text' : 'password'}
                          autoComplete="new-password"
                          value={oldPassword}
                          onChange={(e) => { setOldPassword(e.target.value); setCurrentPwdError(''); }}
                          onBlur={checkCurrentPassword}
                          placeholder="Enter current password"
                          style={{ borderColor: currentPwdError ? 'var(--Profile-red)' : undefined }}
                        />
                        <button type="button" className="ProfileSettingsPwdEye" onClick={() => setShowOld((v) => !v)}><EyeIcon open={showOld} /></button>
                      </div>
                      {currentPwdChecking && <span className="ProfilePwdCheckingHint">Checking…</span>}
                      {currentPwdError && <span className="ProfilePwdMatchNo">{currentPwdError}</span>}
                    </div>
                    <div className="ProfileSettingsPwdField">
                      <label>New Password</label>
                      <div className="ProfileSettingsPwdInputWrap">
                        <input type={showNew ? 'text' : 'password'} autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 characters" />
                        <button type="button" className="ProfileSettingsPwdEye" onClick={() => setShowNew((v) => !v)}><EyeIcon open={showNew} /></button>
                      </div>
                      {sameAsCurrent && (
                        <span className="ProfilePwdMatchNo">New password cannot be the same as your current password.</span>
                      )}
                      {newPassword.length > 0 && (
                        <div className="ProfilePwdStrengthWrap">
                          <div className="ProfilePwdStrengthBar">
                            {[1,2,3,4,5].map((i) => (
                              <div key={i} className="ProfilePwdStrengthSegment" style={{ background: i <= score ? strengthColor : undefined }} />
                            ))}
                          </div>
                          <span className="ProfilePwdStrengthLabel" style={{ color: strengthColor }}>{strengthLabel}</span>
                        </div>
                      )}
                      {newPassword.length > 0 && (
                        <ul className="ProfilePwdReqList">
                          {reqs.map((r) => (
                            <li key={r.label} className={r.met ? 'ProfilePwdReqMet' : 'ProfilePwdReqUnmet'}>
                              <span className="ProfilePwdReqDot">{r.met ? '✓' : '–'}</span> {r.label}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* ── Right column ── */}
                  <div className="ProfilePwdCol">
                    <div className="ProfileSettingsPwdField">
                      <label>Confirm New Password</label>
                      <div className="ProfileSettingsPwdInputWrap">
                        <input
                          type={showConfirm ? 'text' : 'password'}
                          autoComplete="new-password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Repeat new password"
                          style={{ borderColor: passwordsMatch ? 'var(--Profile-green)' : passwordsMismatch ? 'var(--Profile-red)' : undefined }}
                        />
                        <button type="button" className="ProfileSettingsPwdEye" onClick={() => setShowConfirm((v) => !v)}><EyeIcon open={showConfirm} /></button>
                      </div>
                      {passwordsMatch && <span className="ProfilePwdMatchOk">✓ Passwords match</span>}
                      {passwordsMismatch && <span className="ProfilePwdMatchNo">✗ Passwords do not match</span>}
                    </div>

                  </div>
                </div>

                {/* ── Full-width OTP section ── */}
                {fieldsReady && pwdOtpVisible && !pwdOtp.verified && (
                  <div className="ProfileSettingsOtpSection">
                    <p className="ProfileSettingsOtpLabel">Verify your identity to update the password</p>
                    {!pwdOtp.sent && (
                      <div className="ProfileSettingsOtpBtnRow">
                        <button type="button" className="ProfileBtn ProfileBtnOutline ProfileBtnSm" onClick={() => handlePwdSendOtp('email')} disabled={pwdOtp.sending || !otpTargetEmail}>
                          {pwdOtp.sending && pwdOtp.method === 'email' ? '...' : 'Verify via Email'}
                        </button>
                        <button type="button" className="ProfileBtn ProfileBtnOutline ProfileBtnSm" onClick={() => handlePwdSendOtp('phone')} disabled={pwdOtp.sending || !otpTargetPhone}>
                          {pwdOtp.sending && pwdOtp.method === 'phone' ? '...' : 'Verify via Phone'}
                        </button>
                      </div>
                    )}
                    {pwdOtp.sent && (
                      <div className="ProfileSettingsOtpCenter">
                        <p className="ProfileSettingsOtpHint">OTP sent to {pwdOtp.method === 'email' ? otpTargetEmail : `${phoneCode} ${otpTargetPhone}`}</p>
                        <OtpBoxes value={pwdOtp.otp} onChange={pwdOtp.setOtp} />
                        <button type="button" className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={handlePwdVerifyOtp} disabled={pwdOtp.verifying || pwdOtp.otp.length < 6}>{pwdOtp.verifying ? 'Verifying...' : 'Verify OTP'}</button>
                        <div className="ProfileSettingsOtpTimer">
                          {pwdOtp.timerActive
                            ? <span>Resend in {formatTime(pwdOtp.timer)}</span>
                            : <button type="button" className="ProfileSettingsResendBtn" onClick={() => pwdOtp.reset()}>Try again</button>}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {pwdOtp.verified && (
                  <div className="ProfileSettingsVerifiedBadge" style={{ margin: '8px 0' }}>
                    {pwdOtp.method === 'email' ? 'Email Verified' : 'Phone Verified'} ✓
                  </div>
                )}

                {/* ── Full-width bottom ── */}
                {pwdMsg.text && (
                  <div className={pwdMsg.error ? 'ProfileSettingsPwdError' : 'ProfileSettingsPwdSuccess'} style={{ marginTop: 4 }}>{pwdMsg.text}</div>
                )}
                <div className="ProfileSettingsPwdActions">
                  {!pwdOtp.verified && (
                    <button type="button" className="ProfileBtn ProfileBtnPrimary" onClick={() => { if (fieldsReady) setPwdOtpVisible(true); }} disabled={!fieldsReady || pwdSaving}>
                      Verify &amp; Update Password
                    </button>
                  )}
                  {pwdOtp.verified && (
                    <button type="button" className="ProfileBtn ProfileBtnPrimary" onClick={handleChangePassword} disabled={pwdSaving}>
                      {pwdSaving ? <span className="ProfileSpinner" /> : 'Save New Password'}
                    </button>
                  )}
                  <button type="button" className="ProfileBtn ProfileBtnOutline" onClick={() => { setPwdOpen(false); setOldPassword(''); setNewPassword(''); setConfirmPassword(''); setPwdMsg({ text: '', error: false }); pwdOtp.reset(); }}>
                    Cancel
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
        )}

        <div className="ProfileSettingsLogoutWrap ProfileSettingsSpan">
          <button className="ProfileBtn ProfileBtnOutline ProfileBtnSignOut" onClick={onLogout}>
            <Icons.Logout /> Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}